import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ShieldCheck } from "lucide-react";
import {
  acceptClubAdminInvite,
  ApiError,
  describeClubAdminInvite,
  getAuthMode,
  login as apiLogin,
  logout as apiLogout,
  setAuthToken,
} from "@/lib/api";
import { useMe } from "@/features/auth/useMe";
import { applyExplicitLogoutBoundary, unscopedKey } from "@/lib/queryClient";
import { Button } from "@/components/ui/Button";
import { authSearchParam, isLegacyRequested } from "@/features/auth/authPaths";
import { KratosRegisterPanel } from "@/features/auth/kratos/KratosRegisterPanel";
import { invalidateInviteAcceptanceCaches } from "@/features/onboarding/inviteAcceptanceCache";

/**
 * `/invite/club/$token` — club-admin invite-acceptance landing.
 *
 * The super-admin emails a one-time token; the invited admin lands here to
 * become the club's first club_admin. Modeled on `InviteAcceptPage` (team
 * invites) — both now AUTO-accept on the signed-in branch — with one deliberate
 * divergence: the invitee is typically brand-new, so the unauthenticated branch
 * creates the account INLINE (the Kratos `KratosRegisterPanel` renders in place)
 * rather than linking out to `/register` the way the team page does.
 *
 * Token handling: the token comes from the URL path and moves only to the
 * sign-in `next` path, the accept mutation body, and the descriptor GET
 * (`/api/club-admin/invites/{token}` — the same URL-borne exposure class as
 * the emailed link itself). It never reaches Tktic activity/application
 * logs (docs/PRIVACY.md). Two render branches:
 *   - Signed-in user: auto-accepts on mount (a signed-in visitor to a pending
 *     invite URL already intends to accept). The backend binds the invite to
 *     the session's (Kratos-verified) email, so a mismatched account gets a
 *     403 — on which the branch falls back to a manual "Try again" + "Sign in
 *     as someone else". See SignedInBranch.
 *   - Unauthenticated user: creates the account inline. In Kratos mode the
 *     `KratosRegisterPanel` (email/password + verification code) renders in
 *     place with `nextPath` back to this invite, so the post-verify exchange
 *     returns here and SignedInBranch auto-accepts — no /register detour.
 *     Social/OIDC is off inline (its ceremony must anchor on the apex
 *     `/register` callback, docs/KRATOS.md); a "Use social sign-in" link hands
 *     off there. In legacy/dual mode the fallback form still creates a local
 *     account and auto-logs-in via `/api/login`.
 *
 * On success we send them to `/admin` on the current host — the club they
 * now administer — to finish setup (branding, teams, people).
 */
export function ClubInviteAcceptPage() {
  const params = useParams({ from: "/invite/club/$token" });
  const search = useSearch({ from: "/invite/club/$token" });
  const token = decodeURIComponent(params.token);
  const me = useMe();
  const authMode = useQuery({
    queryKey: unscopedKey("auth-mode"),
    queryFn: getAuthMode,
    staleTime: 60_000,
  });
  const invite = useQuery({
    queryKey: unscopedKey("club-invite-info", { token }),
    queryFn: () => describeClubAdminInvite(token),
    retry: false,
    staleTime: 60_000,
  });
  const kratosEnabled = authMode.data?.kratos_enabled ?? false;
  const legacyEnabled = authMode.data
    ? authMode.data.legacy_enabled
    : authMode.isError;
  const legacyRequested =
    isLegacyRequested(search.legacy) ||
    isLegacyRequested(authSearchParam("legacy"));

  if (me.isLoading || invite.isLoading) {
    return (
      <main
        data-testid="club-invite-loading"
        className="min-h-screen flex items-center justify-center text-ink-muted"
      >
        Loading…
      </main>
    );
  }

  const clubName = invite.data?.club_name?.trim() || "your club";
  const unusable =
    invite.isError || (invite.data && invite.data.status !== "pending");

  return (
    <main
      data-testid="club-invite-page"
      className="min-h-screen bg-canvas flex items-center justify-center p-4"
    >
      <section
        aria-label="Accept club admin invite"
        className="w-full max-w-md rounded border border-rule bg-surface p-6 flex flex-col gap-4"
      >
        <header>
          <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
            Club admin invite
          </span>
          <h1 className="text-xl font-display tracking-tight text-ink">
            {unusable ? "This invite can't be used" : `Set up ${clubName}`}
          </h1>
          {unusable ? null : (
            <p className="text-ink-muted text-xs">
              You&rsquo;ve been invited to administer{" "}
              <span className="text-ink">{clubName}</span> — its name, branding,
              teams, and members.
            </p>
          )}
        </header>

        {unusable ? (
          <p
            data-testid="club-invite-unusable"
            className="text-destructive text-xs font-mono"
          >
            {unusableMessage(invite.data?.status, invite.error)}
          </p>
        ) : me.data?.user ? (
          <SignedInBranch
            token={token}
            username={me.data.user.username}
            clubName={clubName}
            authModeReady={!authMode.isPending}
          />
        ) : (
          <UnauthenticatedBranch
            token={token}
            kratosEnabled={kratosEnabled}
            legacyEnabled={legacyEnabled}
            legacyRequested={legacyRequested}
            loadingMode={authMode.isPending}
          />
        )}
      </section>
    </main>
  );
}

function unusableMessage(status: string | undefined, error: unknown): string {
  if (status === "accepted") return "This invite has already been used.";
  if (status === "expired") return "This invite has expired. Ask for a new one.";
  if (status === "revoked") return "This invite was revoked.";
  if (error instanceof ApiError && error.status !== 404) {
    return `Could not load the invite (HTTP ${error.status}).`;
  }
  return "That invite link is invalid or expired.";
}

// ---------------------------------------------------------------------------
// Signed-in branch — AUTO-accept, email-bound server-side
// ---------------------------------------------------------------------------
//
// A signed-in user reaching this pending-invite URL always intends to accept:
// either they just created an account / signed in via `?next=/invite/club/…`,
// or they clicked the emailed link while already signed in. So we fire the
// accept automatically on mount instead of asking for a second explicit
// click — the invitee already committed by clicking "Create account" / the
// email link. The backend still binds the invite to the session's verified
// email, so a MISMATCHED account 403s. TRANSIENT failures (network / 5xx) —
// e.g. the accept not cleanly landing on the create-account return path —
// auto-retry a bounded number of times; TERMINAL failures (wrong account,
// used/expired invite) fall back to the manual controls ("Sign in as someone
// else") so the user is never stuck. See `shouldRetryInviteAccept`.

function SignedInBranch({
  token,
  username,
  clubName,
  authModeReady,
}: {
  token: string;
  username: string;
  clubName: string;
  authModeReady: boolean;
}) {
  const navigate = useNavigate({ from: "/invite/club/$token" });
  const queryClient = useQueryClient();

  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  // Drive button-disable off this local flag, NOT `accept.isPending`: under
  // React StrictMode the auto-accept mutation fires in one mount but is observed
  // in the remounted instance, which can leave `isPending` stuck true after the
  // request settles — permanently disabling the fallback controls. `retrying`
  // is set only by an explicit retry and cleared in onSettled.
  const [retrying, setRetrying] = useState(false);

  // Auto-accept attempt counter — a TRANSIENT failure (network blip, or the
  // request not cleanly landing on the create-account return path) auto-retries
  // a bounded number of times before falling to the manual controls. Without
  // this, a single dropped auto-accept stranded a created-but-role-less account
  // with a still-pending invite (the reported club-admin invite bug).
  const AUTO_ACCEPT_MAX_ATTEMPTS = 4;
  const attemptRef = useRef(0);
  // Pending retry / post-success-navigate timers, cleared on unmount so a
  // scheduled retry can't fire `accept.mutate()` after the user navigated away
  // (e.g. clicked "Sign in as someone else"). Harmless today — mutate() on an
  // unmounted mutation is a no-op — but keeps the timer chain from outliving
  // the component.
  const timersRef = useRef<Set<number>>(new Set());
  const later = (fn: () => void, ms: number) => {
    const id = window.setTimeout(() => {
      timersRef.current.delete(id);
      fn();
    }, ms);
    timersRef.current.add(id);
  };
  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      for (const id of timers) window.clearTimeout(id);
      timers.clear();
    };
  }, []);

  const accept = useMutation({
    mutationFn: () => acceptClubAdminInvite({ token }),
    onSuccess: async () => {
      setSuccess(true);
      setError(null);
      // Club authority and directory access changed; refresh both before
      // routing so the admin shell and a previously visited /teams page agree.
      await invalidateInviteAcceptanceCaches(queryClient);
      later(() => {
        void navigate({ to: "/admin", replace: true });
      }, 600);
    },
    onError: (err) => {
      // Retry transient failures (the create-account return-path race) up to a
      // bound; terminal failures (wrong account, used/expired invite) surface
      // immediately for the manual fallback.
      if (shouldRetryInviteAccept(err) && attemptRef.current < AUTO_ACCEPT_MAX_ATTEMPTS) {
        attemptRef.current += 1;
        const backoffMs = 300 * attemptRef.current;
        later(() => accept.mutate(), backoffMs);
        return;
      }
      setError(formatClubInviteError(err));
    },
    onSettled: () => {
      setRetrying(false);
    },
  });

  // Auto-accept on mount. `useRef` guards against a double-fire from TanStack
  // Router / StrictMode re-renders (same pattern as the /hub and auth gates).
  // Transient errors auto-retry (see onError); terminal errors hand off to the
  // manual "try again" + "Sign in as someone else" fallback below.
  const autoStarted = useRef(false);
  useEffect(() => {
    if (autoStarted.current) return;
    autoStarted.current = true;
    accept.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const switchAccount = async () => {
    const next = clubInvitePathForToken(token);
    try {
      // /api/logout revokes the app session AND the Kratos session.
      await apiLogout();
    } catch {
      // ignore — server might already have revoked
    }
    applyExplicitLogoutBoundary();
    void navigate({ to: "/login", search: { next }, replace: true });
  };

  // Happy path (accepting or accepted): a calm progress line, no button.
  if (!error) {
    return (
      <div data-testid="club-invite-signed-in" className="flex flex-col gap-3">
        <p className="text-sm text-ink-muted">
          Setting up <span className="text-ink">{clubName}</span> for{" "}
          <span className="font-mono text-ink">@{username}</span>…
        </p>
        <p
          data-testid="club-invite-accept-success"
          className="text-good text-xs font-mono"
        >
          {success
            ? "Invite accepted. Taking you to your club’s admin console…"
            : "Accepting your invite…"}
        </p>
      </div>
    );
  }

  // Error fallback: the auto-accept failed (e.g. wrong account 403). Give the
  // user a manual retry + the escape hatch to sign in as the invited account.
  return (
    <div data-testid="club-invite-signed-in" className="flex flex-col gap-3">
      <p className="text-sm text-ink-muted">
        You&rsquo;re signed in as{" "}
        <span className="font-mono text-ink">@{username}</span>. This invite
        only works for the email address it was sent to.
      </p>

      <p
        data-testid="club-invite-accept-error"
        className="text-destructive text-xs font-mono"
      >
        {error}
      </p>

      <div className="flex gap-2">
        <Button
          type="button"
          onClick={() => {
            setError(null);
            setRetrying(true);
            attemptRef.current = 0; // fresh auto-retry budget on an explicit retry
            accept.mutate();
          }}
          disabled={retrying}
          data-testid="club-invite-accept-btn"
        >
          {retrying ? "Accepting…" : "Try again"}
        </Button>
        <button
          type="button"
          onClick={() => void switchAccount()}
          disabled={retrying || !authModeReady}
          data-testid="club-invite-switch-account"
          className="rounded border border-rule px-3 py-2 text-sm font-display uppercase tracking-wider text-ink-muted hover:text-ink hover:bg-field transition-colors disabled:opacity-50"
        >
          Sign in as someone else
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Unauthenticated branch — Kratos entry, legacy create-account fallback
// ---------------------------------------------------------------------------

function UnauthenticatedBranch({
  token,
  kratosEnabled,
  legacyEnabled,
  legacyRequested,
  loadingMode,
}: {
  token: string;
  kratosEnabled: boolean;
  legacyEnabled: boolean;
  legacyRequested: boolean;
  loadingMode: boolean;
}) {
  const showLegacyCreate = legacyEnabled && (!kratosEnabled || legacyRequested);
  const showKratosEntry = kratosEnabled && !legacyRequested && !loadingMode;
  const next = clubInvitePathForToken(token);

  return (
    <div className="flex flex-col gap-4">
      {loadingMode ? (
        <p
          data-testid="club-invite-auth-mode-loading"
          className="font-mono text-xs text-ink-muted"
        >
          Checking account options...
        </p>
      ) : null}
      {showKratosEntry ? (
        <section
          data-testid="club-invite-kratos-entry"
          className="rounded border border-rule bg-canvas p-4"
        >
          <div className="flex items-start gap-3">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded bg-accent text-accent-ink">
              <ShieldCheck aria-hidden="true" className="h-4 w-4" />
            </span>
            <div className="min-w-0">
              <h2 className="font-display text-base font-semibold text-ink">
                Create your account
              </h2>
              <p className="mt-1 text-xs leading-5 text-ink-muted">
                Use the email this invite was sent to. After you verify it,
                Tktic brings you straight back here to finish accepting.
              </p>
            </div>
          </div>
          {/* Inline Kratos register (email/password + verification code) —
              `nextPath` is this same invite URL, so once the app session
              exists the exchange bounces back here and SignedInBranch
              auto-accepts. Social/OIDC is deliberately OFF: its ceremony must
              anchor on the apex `/register` callback (docs/KRATOS.md), so the
              social link below hands off there instead of embedding it. */}
          <div className="mt-4" data-testid="club-invite-kratos-register">
            <KratosRegisterPanel nextPath={next} socialLoginEnabled={false} />
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-rule pt-3 text-[0.65rem]">
            <Link
              to="/login"
              search={{ next }}
              data-testid="club-invite-kratos-login"
              className="rounded px-1 py-0.5 font-mono text-accent hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            >
              Already have an account? Sign in
            </Link>
            <Link
              to="/register"
              search={{ next }}
              data-testid="club-invite-kratos-social"
              className="rounded px-1 py-0.5 font-mono text-ink-muted hover:text-ink hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            >
              Use social sign-in
            </Link>
            {legacyEnabled ? (
              <a
                href={`${next}?legacy=1`}
                data-testid="club-invite-legacy-link"
                className="rounded px-1 py-0.5 font-mono text-ink-muted hover:text-ink hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
              >
                Use legacy account form
              </a>
            ) : null}
          </div>
        </section>
      ) : null}
      {showLegacyCreate ? <CreateAccountBranch token={token} /> : null}
    </div>
  );
}

function CreateAccountBranch({ token }: { token: string }) {
  const navigate = useNavigate({ from: "/invite/club/$token" });
  const queryClient = useQueryClient();

  const [username, setUsername] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const next = clubInvitePathForToken(token);
  const signInPath = `/login?next=${encodeURIComponent(next)}`;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    setSuccess(null);
    const u = username.trim();
    if (!u) {
      setError("Username is required.");
      return;
    }
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    setSubmitting(true);
    // Capture the password locally then clear React state before the network
    // calls — the in-memory value lives only for the two API calls below
    // (same privacy invariant as the team invite page).
    const pw = password;
    setPassword("");
    try {
      await acceptClubAdminInvite({
        token,
        username: u,
        password: pw,
        display_name: displayName.trim() || undefined,
      });
      setSuccess("Account created. Signing you in…");
      try {
        const loginResp = await apiLogin({ username: u, password: pw });
        setAuthToken(loginResp.token);
        await queryClient.invalidateQueries();
        window.setTimeout(() => {
          void navigate({ to: "/admin", replace: true });
        }, 600);
      } catch {
        setError(
          "Account created, but automatic sign-in failed. Sign in with your new account to continue.",
        );
        window.setTimeout(() => {
          void navigate({ to: "/login", replace: true });
        }, 1500);
      }
    } catch (err) {
      setError(formatClubInviteError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      data-testid="club-invite-create-account"
      onSubmit={handleSubmit}
      noValidate
      className="flex flex-col gap-3"
    >
      <p className="text-sm text-ink-muted">
        Create an account to administer this club. Already have one?{" "}
        <a
          href={signInPath}
          data-testid="club-invite-sign-in-instead"
          className="text-accent hover:underline"
        >
          Sign in instead
        </a>
        .
      </p>

      <label className="flex flex-col gap-1 text-xs">
        <span className="font-display uppercase tracking-wider text-ink-muted">
          Username
        </span>
        <input
          type="text"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          autoFocus
          required
          minLength={1}
          maxLength={100}
          autoComplete="username"
          data-testid="club-invite-create-username"
          className="rounded border border-rule bg-canvas px-2 py-1.5 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-accent"
        />
      </label>
      <label className="flex flex-col gap-1 text-xs">
        <span className="font-display uppercase tracking-wider text-ink-muted">
          Display name
        </span>
        <input
          type="text"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          maxLength={200}
          autoComplete="name"
          data-testid="club-invite-create-display-name"
          className="rounded border border-rule bg-canvas px-2 py-1.5 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-accent"
        />
        <span className="text-[0.6rem] font-mono text-ink-muted">
          Optional. Visible to your club&rsquo;s members.
        </span>
      </label>
      <label className="flex flex-col gap-1 text-xs">
        <span className="font-display uppercase tracking-wider text-ink-muted">
          Choose a password
        </span>
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          minLength={8}
          maxLength={200}
          autoComplete="new-password"
          data-testid="club-invite-create-password"
          className="rounded border border-rule bg-canvas px-2 py-1.5 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-accent"
        />
        <span className="text-[0.6rem] font-mono text-ink-muted">
          At least 8 characters.
        </span>
      </label>

      {error ? (
        <p
          data-testid="club-invite-create-error"
          className="text-destructive text-xs font-mono"
        >
          {error}
        </p>
      ) : null}
      {success ? (
        <p
          data-testid="club-invite-create-success"
          className="text-good text-xs font-mono"
        >
          {success}
        </p>
      ) : null}

      <Button
        type="submit"
        disabled={submitting}
        data-testid="club-invite-create-submit"
        className="self-start"
      >
        {submitting ? "Creating…" : "Create account & set up club"}
      </Button>
    </form>
  );
}

function clubInvitePathForToken(token: string): `/invite/club/${string}` {
  return `/invite/club/${encodeURIComponent(token)}`;
}

/**
 * Whether a failed invite-accept is worth retrying. The create-account return
 * path can land the auto-accept before the request cleanly reaches the server
 * (or hit a transient network blip), stranding a created-but-role-less account
 * with a still-pending invite. Those transient failures SHOULD retry. Terminal
 * failures — wrong/unverified account (403), invalid/used/expired invite
 * (404/409/410), or a lost session (401) — cannot be fixed by retrying with the
 * same session, so they fall to the manual "try again / sign in as someone
 * else" controls instead.
 */
export function shouldRetryInviteAccept(err: unknown): boolean {
  if (err instanceof ApiError) {
    // Only server errors (5xx) are transient; every 4xx here is terminal.
    return err.status >= 500;
  }
  // Non-ApiError = network/abort/parse failure — transient, retry.
  return true;
}

export function formatClubInviteError(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 403) {
      // The backend distinguishes two accept-blocking causes; surface which
      // one so the user knows what to fix instead of a generic "not a member".
      const detail = typeof err.detail === "string" ? err.detail : "";
      if (detail === "Invite email must be verified") {
        return "Verify your account's email address first, then reopen this invite link to accept.";
      }
      // "Invite email does not match user" (and any other 403).
      return "This invite was sent to a different email address than the account you're signed in as. Sign in with the invited account and try again.";
    }
    if (err.status === 404) return "That invite is invalid or expired.";
    if (err.status === 409) return "This invite has already been used.";
    if (err.status === 410) return "This invite has expired.";
    return typeof err.detail === "string"
      ? err.detail
      : `Could not accept the invite (HTTP ${err.status}).`;
  }
  return "Network error. Try again in a moment.";
}
